import dayjs from "dayjs";

const WEEKDAY_LABELS = ["월", "화", "수", "목", "금", "토", "일"];

const emptyCell = (overrides = {}) => ({
	status: "empty",
	tooltip: "",
	has_excuse: false,
	excuse_category: null,
	worked_on_weekly_off: false,
	worked_on_holiday: false,
	clock_time: null,
	holiday_kind: null,
	half_day: null,
	override_kind: null,
	can_override: false,
	shift_label: null,
	late_minutes: 0,
	...overrides,
});

const summarize = (cells) => {
	const summary = {
		present: 0,
		late: 0,
		absent: 0,
		holiday: 0,
		on_leave: 0,
		annual_leave: 0,
		sick_leave: 0,
		menstrual_leave: 0,
		business_trip: 0,
		excuse: 0,
		half_day: 0,
		late_minutes: 0,
	};
	Object.values(cells).forEach((cell) => {
		if (cell.status in summary) {
			summary[cell.status] += 1;
		}
		if (cell.has_excuse) {
			summary.excuse += 1;
		}
		summary.late_minutes += Number(cell.late_minutes) || 0;
	});
	return summary;
};

const MOCK_PEOPLE = [
	{
		id: 1,
		user_id: 101,
		name: "김목사",
		attendance_code: "M001",
		employment_status: "재직",
		job: "목사",
		gender: "남",
		sort_at_bottom: false,
		employee_team_names: ["당회"],
		is_security: false,
		pattern: "leader",
	},
	{
		id: 2,
		user_id: 102,
		name: "이전도",
		attendance_code: "P001",
		employment_status: "재직",
		job: "전도사",
		gender: "남",
		sort_at_bottom: false,
		employee_team_names: ["교육국"],
		is_security: false,
		pattern: "normal",
	},
	{
		id: 3,
		user_id: 103,
		name: "박여전",
		attendance_code: "P002",
		employment_status: "재직",
		job: "전도사",
		gender: "여",
		sort_at_bottom: false,
		employee_team_names: ["새가족"],
		is_security: false,
		pattern: "leave",
	},
	{
		id: 4,
		user_id: 104,
		name: "최직원",
		attendance_code: "S001",
		employment_status: "재직",
		job: "교직원",
		gender: "남",
		sort_at_bottom: false,
		employee_team_names: ["총무"],
		is_security: false,
		pattern: "late",
	},
	{
		id: 5,
		user_id: 105,
		name: "정여직",
		attendance_code: "S002",
		employment_status: "재직",
		job: "교직원",
		gender: "여",
		sort_at_bottom: false,
		employee_team_names: ["행정"],
		is_security: false,
		pattern: "excuse",
	},
	{
		id: 6,
		user_id: 106,
		name: "한경비",
		attendance_code: "G001",
		employment_status: "재직",
		job: "교직원",
		gender: "남",
		sort_at_bottom: false,
		employee_team_names: ["경비실"],
		is_security: true,
		pattern: "security",
	},
];

const buildCellForDay = (person, day, holidayName) => {
	const weekday = day.day(); // 0=일
	const dayOfMonth = day.date();

	if (weekday === 0 && !person.is_security) {
		const hasExcuse = person.pattern === "excuse" && dayOfMonth === 5;
		return emptyCell({
			status: "holiday",
			holiday_kind: "주일",
			has_excuse: hasExcuse,
			excuse_category: hasExcuse ? "단말기 오류" : null,
			tooltip: hasExcuse ? "단말기 오류 · 카드 인식 실패" : "",
		});
	}

	if (holidayName) {
		const hasExcuse = person.pattern === "excuse" && dayOfMonth % 7 === 3;
		if (person.pattern === "leader" || person.pattern === "normal") {
			return emptyCell({
				status: "present",
				holiday_kind: holidayName,
				worked_on_holiday: true,
				clock_time: "09:00:00",
				tooltip: `${holidayName} 출근 09:00`,
				can_override: true,
			});
		}
		return emptyCell({
			status: "holiday",
			holiday_kind: holidayName,
			has_excuse: hasExcuse,
			excuse_category: hasExcuse ? "개인 사정" : null,
			tooltip: hasExcuse
				? `${holidayName} · 개인 사정 · 병원 진료`
				: holidayName,
			can_override: !hasExcuse,
		});
	}

	if (person.pattern === "leave" && [8, 9].includes(dayOfMonth)) {
		const date = day.format("YYYY-MM-DD");
		return emptyCell({
			status: "annual_leave",
			tooltip: "연차",
			leave_applications: [
				{
					id: `annual-${person.user_id}-${date}`,
					leave_type: "연차",
					user_name: person.name,
					user_job: person.job,
					user_gender: person.gender,
					application_date: day.subtract(7, "day").format("YYYY-MM-DD"),
					leave_date: date,
					start_date: day.date(8).format("YYYY-MM-DD"),
					end_date: day.date(9).format("YYYY-MM-DD"),
					reason: "개인 사정",
					additional_users_info: [],
					approval_steps: [],
					team_leader_status: "approved",
					office_director_status: "approved",
					church_leader_status: "not_required",
				},
			],
		});
	}

	if (person.pattern === "leave" && dayOfMonth === 15) {
		const date = day.format("YYYY-MM-DD");
		return emptyCell({
			status: "half_day",
			half_day: "am",
			tooltip: "오전 반차",
			clock_time: "13:05:00",
			leave_applications: [
				{
					id: `half-${person.user_id}-${date}`,
					leave_type: "오전반차",
					user_name: person.name,
					user_job: person.job,
					user_gender: person.gender,
					application_date: day.subtract(3, "day").format("YYYY-MM-DD"),
					leave_date: date,
					reason: "병원 진료",
					additional_users_info: [],
					approval_steps: [],
					team_leader_status: "approved",
					office_director_status: "approved",
					church_leader_status: "approved",
				},
			],
		});
	}

	if (person.pattern === "leave" && dayOfMonth === 20) {
		const date = day.format("YYYY-MM-DD");
		return emptyCell({
			status: "business_trip",
			tooltip: "부산",
			leave_applications: [
				{
					id: `trip-${person.user_id}-${date}`,
					leave_type: "출장",
					user_name: person.name,
					user_job: person.job,
					user_gender: person.gender,
					application_date: day.subtract(10, "day").format("YYYY-MM-DD"),
					leave_date: date,
					start_date: date,
					end_date: date,
					reason: "지방 집회",
					trip_title: "지방 집회",
					trip_background: "지역 교회 방문",
					trip_location: "부산",
					trip_includes_lodging: false,
					trip_transport: "열차",
					trip_note: "",
					additional_users_info: [],
					approval_steps: [],
					team_leader_status: "approved",
					office_director_status: "approved",
					church_leader_status: "approved",
				},
			],
		});
	}

	if (person.pattern === "late" && [3, 10, 17].includes(dayOfMonth)) {
		const lateMinutes = dayOfMonth === 3 ? 12 : dayOfMonth === 10 ? 45 : 75;
		return emptyCell({
			status: "late",
			clock_time: dayOfMonth === 17 ? "10:15:00" : "09:20:00",
			tooltip: `출근 ${dayOfMonth === 17 ? "10:15" : "09:20"} (지각)`,
			late_minutes: lateMinutes,
		});
	}

	if (person.pattern === "late" && dayOfMonth === 21) {
		const date = day.format("YYYY-MM-DD");
		return emptyCell({
			status: "sick_leave",
			tooltip: "몸살",
			leave_applications: [
				{
					id: `sick-${person.user_id}-${date}`,
					leave_type: "병가",
					user_name: person.name,
					user_job: person.job,
					user_gender: person.gender,
					application_date: date,
					leave_date: date,
					start_date: date,
					end_date: date,
					reason: "몸살",
					additional_users_info: [],
					approval_steps: [],
					team_leader_status: "approved",
					office_director_status: "pending",
					church_leader_status: "pending",
				},
			],
		});
	}

	if (person.pattern === "late" && dayOfMonth === 22) {
		return emptyCell({
			status: "absent",
			tooltip: "결석",
		});
	}

	if (person.pattern === "excuse" && dayOfMonth === 12) {
		const date = day.format("YYYY-MM-DD");
		return emptyCell({
			status: "present",
			has_excuse: true,
			excuse_category: "단말기 오류",
			clock_time: "09:40:00",
			tooltip: "단말기 오류 · 카드 인식 실패 (출근 09:40)",
			leave_applications: [
				{
					id: `excuse-${person.user_id}-${date}`,
					leave_type: "사유서",
					user_name: person.name,
					user_job: person.job,
					user_gender: person.gender,
					application_date: date,
					leave_date: date,
					start_date: date,
					end_date: date,
					reason_category: "단말기 오류",
					reason: "카드 인식 실패",
					additional_users_info: [],
				},
			],
		});
	}

	if (person.pattern === "security") {
		if (weekday === 0) {
			return emptyCell({
				status: "present",
				clock_time: "07:55:00",
				tooltip: "출근 07:55 · 근무 A조",
				shift_label: "A조",
			});
		}
		if (weekday === 6) {
			return emptyCell({
				status: "holiday",
				holiday_kind: "정기휴일",
				tooltip: "정기휴무 · 근무 OFF",
				shift_label: "OFF",
			});
		}
	}

	if (day.isAfter(dayjs(), "day")) {
		return emptyCell({
			status: "pending",
			tooltip: "",
		});
	}

	return emptyCell({
		status: "present",
		clock_time: "08:55:00",
		tooltip: "출근 08:55",
	});
};

/**
 * 로컬에서 MonthlyAttendance를 확인할 때 쓰는 월별 근태 mock.
 * API 실패 시(개발 환경) 또는 URL에 ?mockAttendance=1 이 있으면 사용합니다.
 */
export const buildMonthlyAttendanceMock = (year, month) => {
	const start = dayjs(`${year}-${String(month).padStart(2, "0")}-01`);
	const end = start.endOf("month");
	const holidayByDay = {
		1: month === 1 ? "신정" : null,
		3: month === 10 ? "개천절" : null,
		9: month === 10 ? "한글날" : null,
	};

	const dates = [];
	const cellsByUser = {};
	MOCK_PEOPLE.forEach((person) => {
		cellsByUser[person.user_id] = {};
	});

	let cursor = start.startOf("day");
	while (!cursor.isAfter(end, "day")) {
		const weekday = cursor.day() === 0 ? 6 : cursor.day() - 1;
		const holidayName = holidayByDay[cursor.date()] || null;
		dates.push({
			date: cursor.format("YYYY-MM-DD"),
			weekday,
			weekday_label: WEEKDAY_LABELS[weekday],
			work_type: weekday === 6 ? null : "normal",
			work_type_id: weekday === 6 ? null : 1,
			work_type_name: weekday === 6 ? null : "일반근무",
			start_time: weekday === 6 ? null : "09:00:00",
			has_work_day: weekday !== 6,
			work_day_id: weekday === 6 ? null : cursor.date(),
			holiday_name: holidayName,
		});

		MOCK_PEOPLE.forEach((person) => {
			cellsByUser[person.user_id][cursor.format("YYYY-MM-DD")] =
				buildCellForDay(person, cursor, holidayName);
		});

		cursor = cursor.add(1, "day");
	}

	const employees = MOCK_PEOPLE.map((person) => {
		const cells = cellsByUser[person.user_id];
		const { pattern, ...rest } = person;
		return {
			...rest,
			cells,
			summary: summarize(cells),
		};
	});

	return {
		start: start.format("YYYY-MM-DD"),
		end: end.format("YYYY-MM-DD"),
		dates,
		employees,
		_mock: true,
	};
};

export const shouldUseMonthlyAttendanceMock = () => {
	if (typeof window === "undefined") {
		return false;
	}
	const params = new URLSearchParams(window.location.search);
	if (params.get("mockAttendance") === "1") {
		return true;
	}
	return process.env.NODE_ENV === "development";
};
